import { useState, useEffect } from "react";
import { FileDown, Package, ArrowLeftRight, ShoppingCart, Clock, CalendarDays, RotateCcw, DatabaseBackup } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, rupiah } from "../lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const reports = [
  { kind: "items", label: "Laporan Stok Barang", desc: "Semua barang, stok & nilai stok", icon: Package, color: "var(--primary)", dated: false },
  { kind: "transactions", label: "Laporan Transaksi", desc: "Riwayat masuk, keluar & retur", icon: ArrowLeftRight, color: "var(--keluar)", dated: true },
  { kind: "sales", label: "Laporan Penjualan", desc: "Rekap penjualan + potongan", icon: ShoppingCart, color: "var(--masuk)", dated: true },
  { kind: "attendance", label: "Laporan Absensi", desc: "Masuk, pulang, telat, tidak masuk", icon: Clock, color: "var(--retur)", dated: true },
];

export default function Reports() {
  const [loading, setLoading] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [daily, setDaily] = useState(new Date().toISOString().slice(0, 10));
  const [returnsMonth, setReturnsMonth] = useState(new Date().toISOString().slice(0, 7));
  const [returnsData, setReturnsData] = useState(null);

  useEffect(() => {
    api.get(`/reports/returns?month=${returnsMonth}`).then((r) => setReturnsData(r.data)).catch(() => setReturnsData(null));
  }, [returnsMonth]);

  const downloadReturns = async (fmt) => {
    setLoading(`retur-${fmt}`);
    try {
      const params = new URLSearchParams({ kind: "returns", fmt, month: returnsMonth });
      const res = await fetch(`${API}/reports/export?${params.toString()}`, {
        credentials: "include",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (!res.ok) throw new Error("Gagal mengunduh");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `laporan_retur_${returnsMonth}.${fmt === "pdf" ? "pdf" : "xlsx"}`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Laporan diunduh");
    } catch (err) { toast.error(errMsg(err)); }
    finally { setLoading(""); }
  };

  const download = async (kind, fmt, s = start, e = end, name = kind) => {    setLoading(`${name}-${fmt}`);
    try {
      const params = new URLSearchParams({ kind, fmt });
      if (s) params.set("start", s);
      if (e) params.set("end", e);
      const res = await fetch(`${API}/reports/export?${params.toString()}`, {
        credentials: "include",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (!res.ok) throw new Error("Gagal mengunduh");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `laporan_${name}.${fmt === "pdf" ? "pdf" : "xlsx"}`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Laporan diunduh");
    } catch (err) { toast.error(errMsg(err)); }
    finally { setLoading(""); }
  };

  const backupAll = async () => {
    setLoading("backup");
    try {
      const res = await fetch(`${API}/backup/all`, {
        credentials: "include",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (!res.ok) throw new Error("Gagal membuat backup");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `backup_mainan_dede_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Backup semua data berhasil diunduh");
    } catch (err) { toast.error(errMsg(err)); }
    finally { setLoading(""); }
  };

  return (
    <div className="space-y-5 animate-fadeup">
      <div>
        <h1 className="text-2xl sm:text-3xl font-display font-extrabold">Laporan</h1>
        <p className="text-[#52525B] text-sm">Filter tanggal, lalu unduh Excel atau PDF</p>
      </div>

      <div className="nb-card p-5 flex items-center gap-4 flex-wrap" data-testid="report-backup" style={{ background: "var(--primary)" }}>
        <div className="w-12 h-12 border-2 border-[#0A0A0A] rounded-lg flex items-center justify-center bg-white shrink-0"><DatabaseBackup className="w-6 h-6" strokeWidth={2.4} /></div>
        <div className="flex-1 min-w-[180px]">
          <h2 className="font-display font-bold text-lg">Backup Semua Data</h2>
          <p className="text-sm text-[#3f3f46]">Satu file Excel berisi semua sheet: Stok, Penjualan, Mutasi, Hutang, Barang Rusak & Absensi</p>
          <p className="text-xs text-[#3f3f46] mt-1">🗓️ Otomatis dikirim ke email tiap hari terakhir bulan, jam 23:59 WIB</p>
        </div>
        <button data-testid="backup-all-button" onClick={backupAll} disabled={loading === "backup"} className="nb-btn px-5 py-3 flex items-center gap-2 bg-white text-sm font-bold disabled:opacity-60">
          <DatabaseBackup className="w-5 h-5" /> {loading === "backup" ? "Menyiapkan…" : "Unduh Backup (Excel)"}
        </button>
      </div>

      <div className="nb-card p-4" data-testid="report-filter">
        <span className="label-tag">Filter Tanggal (opsional)</span>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <div>
            <label className="label-tag block mb-1">Dari</label>
            <input data-testid="filter-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="nb-input" />
          </div>
          <div>
            <label className="label-tag block mb-1">Sampai</label>
            <input data-testid="filter-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="nb-input" />
          </div>
        </div>
      </div>

      <div className="nb-card p-5" data-testid="report-daily" style={{ background: "var(--masuk)" }}>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-11 h-11 border-2 border-[#0A0A0A] rounded-lg flex items-center justify-center bg-white shrink-0"><CalendarDays className="w-5 h-5" strokeWidth={2.4} /></div>
          <div>
            <h2 className="font-display font-bold text-lg">Laporan Harian</h2>
            <p className="text-sm text-[#3f3f46]">Rekap penjualan untuk 1 hari — unduh Excel/PDF</p>
          </div>
        </div>
        <label className="label-tag block mb-1">Tanggal</label>
        <input data-testid="daily-date" type="date" value={daily} onChange={(e) => setDaily(e.target.value)} className="nb-input" />
        <div className="flex gap-2 mt-3">
          <button data-testid="export-daily-excel" onClick={() => download("sales", "excel", daily, daily, "harian")} disabled={loading === "harian-excel"} className="nb-btn flex-1 py-2.5 flex items-center justify-center gap-2 bg-white text-sm"><FileDown className="w-4 h-4" /> Excel</button>
          <button data-testid="export-daily-pdf" onClick={() => download("sales", "pdf", daily, daily, "harian")} disabled={loading === "harian-pdf"} className="nb-btn flex-1 py-2.5 flex items-center justify-center gap-2 text-[#0A0A0A] text-sm" style={{ background: "var(--primary)" }}><FileDown className="w-4 h-4" /> PDF</button>
        </div>
      </div>

      <div className="nb-card p-5" data-testid="report-returns" style={{ background: "var(--biru)", color: "#fff" }}>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-11 h-11 border-2 border-[#0A0A0A] rounded-lg flex items-center justify-center bg-white shrink-0"><RotateCcw className="w-5 h-5 text-[#0A0A0A]" strokeWidth={2.4} /></div>
          <div>
            <h2 className="font-display font-bold text-lg">Laporan Retur</h2>
            <p className="text-sm text-white/90">Total retur per channel & produk yang sering diretur</p>
          </div>
        </div>
        <label className="label-tag block mb-1 text-white/90">Bulan</label>
        <input data-testid="returns-month" type="month" value={returnsMonth} onChange={(e) => setReturnsMonth(e.target.value)} className="nb-input text-[#0A0A0A]" />

        {returnsData && (
          <div className="mt-4 grid sm:grid-cols-3 gap-3">
            <div className="bg-white text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-lg p-3" data-testid="returns-total-qty">
              <span className="label-tag">Total Barang Diretur</span>
              <div className="font-display font-extrabold text-2xl mt-1">{returnsData.summary.qty}</div>
            </div>
            <div className="bg-white text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-lg p-3" data-testid="returns-total-omzet">
              <span className="label-tag">Nilai Retur (Omzet)</span>
              <div className="font-display font-extrabold text-2xl mt-1">{rupiah(returnsData.summary.omzet)}</div>
            </div>
            <div className="bg-white text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-lg p-3" data-testid="returns-count">
              <span className="label-tag">Jumlah Kejadian Retur</span>
              <div className="font-display font-extrabold text-2xl mt-1">{returnsData.summary.count}</div>
            </div>
          </div>
        )}

        {returnsData && returnsData.summary.count === 0 && (
          <p className="mt-3 text-sm text-white/90" data-testid="returns-empty">Belum ada retur pada bulan ini.</p>
        )}

        {returnsData && returnsData.by_channel.length > 0 && (
          <div className="mt-4 grid md:grid-cols-2 gap-4">
            <div className="bg-white text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-lg p-3" data-testid="returns-by-channel">
              <h3 className="font-bold text-sm mb-2">Retur per Channel</h3>
              <div className="space-y-1.5">
                {returnsData.by_channel.map((c) => (
                  <div key={c.channel} className="flex items-center justify-between text-sm border-b border-[#0A0A0A]/10 pb-1">
                    <span className="font-semibold">{c.channel}</span>
                    <span className="text-[#52525B]">{c.qty} pcs · {rupiah(c.omzet)}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-lg p-3" data-testid="returns-top-products">
              <h3 className="font-bold text-sm mb-2">Produk Paling Sering Diretur</h3>
              <div className="space-y-1.5">
                {returnsData.by_product.slice(0, 5).map((p, i) => (
                  <div key={i} className="flex items-center justify-between text-sm border-b border-[#0A0A0A]/10 pb-1">
                    <span className="font-semibold truncate mr-2">{p.item_name}{p.warna ? ` (${p.warna})` : ""}</span>
                    <span className="text-[#52525B] shrink-0">{p.qty} pcs</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="flex gap-2 mt-4">
          <button data-testid="export-returns-excel" onClick={() => downloadReturns("excel")} disabled={loading === "retur-excel"} className="nb-btn flex-1 py-2.5 flex items-center justify-center gap-2 bg-white text-[#0A0A0A] text-sm"><FileDown className="w-4 h-4" /> Excel</button>
          <button data-testid="export-returns-pdf" onClick={() => downloadReturns("pdf")} disabled={loading === "retur-pdf"} className="nb-btn flex-1 py-2.5 flex items-center justify-center gap-2 text-[#0A0A0A] text-sm" style={{ background: "var(--primary)" }}><FileDown className="w-4 h-4" /> PDF</button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 items-start">
        {reports.map((r) => (
          <div key={r.kind} data-testid={`report-${r.kind}`} className="nb-card nb-hover p-5">
            <div className="w-12 h-12 border-2 border-[#0A0A0A] rounded-lg flex items-center justify-center mb-4" style={{ background: r.color }}>
              <r.icon className="w-6 h-6" strokeWidth={2.4} />
            </div>
            <h2 className="font-display font-bold text-lg">{r.label}</h2>
            <p className="text-sm text-[#52525B] mb-1">{r.desc}</p>
            {!r.dated && <p className="text-xs text-[#52525B] mb-3">*abaikan filter tanggal</p>}
            <div className="flex gap-2 mt-3">
              <button data-testid={`export-${r.kind}-excel`} onClick={() => download(r.kind, "excel")} disabled={loading === `${r.kind}-excel`}
                className="nb-btn flex-1 py-2.5 flex items-center justify-center gap-2 bg-white text-sm">
                <FileDown className="w-4 h-4" /> Excel
              </button>
              <button data-testid={`export-${r.kind}-pdf`} onClick={() => download(r.kind, "pdf")} disabled={loading === `${r.kind}-pdf`}
                className="nb-btn flex-1 py-2.5 flex items-center justify-center gap-2 text-[#0A0A0A] text-sm" style={{ background: "var(--primary)" }}>
                <FileDown className="w-4 h-4" /> PDF
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
